import React, { useState, useEffect, useRef, useCallback } from 'react';
import litigesApi from '../services/litiges';
import InlineStatusSelect from './InlineStatusSelect';
import litigeDocsApi from '../services/litige-documents';
import statutsLitigesApi from '../services/statuts-litiges';
import referentielsApi from '../services/referentiels';
import concessionsApi from '../services/concessions';
import api from '../services/api';
import './Litiges.css';
import { PlanificationCell, useDossierPlanning } from './shared/DossierPlanification';
import {
  getPdfjsLib,
  normalizeDoc,
  compressFile,
  compressPdfClientSide,
} from '../utils/documents';
import {
  PdfThumbnail,
  DocumentViewer,
  CommentDocThumb,
} from './shared/DocumentViewer';

import { texteTheme } from '../utils/theme';
// ─── Helpers partagés (Brand, Concession, SearchableSelect) ──────────────────
const BRAND_LOGOS = {
  'Honda':   'https://upload.wikimedia.org/wikipedia/commons/thumb/3/38/Honda.svg/120px-Honda.svg.png',
  'Suzuki':  'https://upload.wikimedia.org/wikipedia/commons/thumb/3/31/Suzuki_Motor_Corporation_logo.svg/120px-Suzuki_Motor_Corporation_logo.svg.png',
  'Nissan':  'https://upload.wikimedia.org/wikipedia/commons/thumb/2/23/Nissan_2020_logo.svg/120px-Nissan_2020_logo.svg.png',
  'Opel':    'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2b/Opel_logo_2023.svg/120px-Opel_logo_2023.svg.png',
  'Mazda':   'https://upload.wikimedia.org/wikipedia/commons/thumb/4/46/Mazda_logo_2024.svg/120px-Mazda_logo_2024.svg.png',
  'Ford':    'https://upload.wikimedia.org/wikipedia/commons/thumb/3/3e/Ford_logo_flat.svg/120px-Ford_logo_flat.svg.png',
  'Renault': 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/49/Renault_2021_Text.svg/120px-Renault_2021_Text.svg.png',
  'Peugeot': 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f7/Peugeot_Logo.svg/120px-Peugeot_Logo.svg.png',
  'Citroën': 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/fd/Citroen_2022_Logo.svg/120px-Citroen_2022_Logo.svg.png',
  'Toyota':  'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9d/Toyota_carlogo.svg/120px-Toyota_carlogo.svg.png',
  'Hyundai': 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b7/Hyundai_symbol.svg/120px-Hyundai_symbol.svg.png',
  'Skoda':   'https://upload.wikimedia.org/wikipedia/commons/thumb/5/52/%C5%A0koda_Auto.svg/120px-%C5%A0koda_Auto.svg.png',
};

const CONCESSION_LABELS = { 'BLUE': 'Blue Auto', 'CENT': 'Centre Auto', 'SOGU': 'Soguava' };

// ─── Dimensions processuelles ─────────────────────────────────────────────────
const DIMENSIONS = [
  { key: 'amiable',              label: 'Amiable',              abbr: 'A', couleur: '#22c55e' },
  { key: 'mediation',            label: 'Médiation',            abbr: 'M', couleur: '#3b82f6' },
  { key: 'protection_juridique', label: 'Protection juridique', abbr: 'P', couleur: '#8b5cf6' },
  { key: 'expertise',            label: 'Expertise',            abbr: 'E', couleur: '#f97316' },
  { key: 'judiciaire',           label: 'Judiciaire',           abbr: 'J', couleur: '#ef4444' },
  { key: 'cloture',              label: 'Clôture',              abbr: 'C', couleur: '#1f2937' },
];

function BrandLogo({ nom }) {
  const [failed, setFailed] = React.useState(false);
  const url = BRAND_LOGOS[nom];
  if (!url || failed) return <span className="brand-btn-fallback">{nom.charAt(0)}</span>;
  return <img src={url} alt={nom} onError={() => setFailed(true)} />;
}

function SearchableInput({ value, onChange, options, placeholder }) {
  const [inputVal, setInputVal] = useState('');
  const [open, setOpen] = useState(false);
  const displayLabel = value ? (options.find(o => o.value === value)?.display || value) : '';
  const filtered = options.filter(o => o.search.toLowerCase().includes(inputVal.toLowerCase()));
  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        value={open ? inputVal : displayLabel}
        onChange={e => setInputVal(e.target.value)}
        onFocus={() => { setOpen(true); setInputVal(''); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: '6px', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
      />
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: '6px', zIndex: 1000, maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          {filtered.length === 0
            ? <div style={{ padding: '10px', color: 'var(--text-muted)', fontSize: '13px' }}>Aucun résultat</div>
            : filtered.map(o => (
              <div key={o.value} onMouseDown={() => { onChange(o.value); setOpen(false); }}
                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-primary)' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >{o.label}</div>
            ))}
        </div>
      )}
    </div>
  );
}

// Helpers PDF/document partagés via ../utils/documents et ./shared/DocumentViewer

function Litiges({ user, onOpenVehicule = () => {}, editMode = false, setHeaderAction, prefill = null, onPrefillConsumed = () => {} }) {
  // Colonne Planification : ouverture du dossier atelier du vehicule en litige.
  const { ouvrir: ouvrirDossier, modale: modaleDossier } = useDossierPlanning({
    user, editMode, onEnregistre: () => loadData(),
  });
  const isOperateur = user?.role === 'operateur';

  const [litiges, setLitiges] = useState([]);
  const [archives, setArchives] = useState([]);
  const [statuts, setStatuts] = useState([]);
  const [responsables, setResponsables] = useState([]);
  const [marques, setMarques] = useState([]);
  const [modeles, setModeles] = useState([]);
  const [concessions, setConcessions] = useState([]);
  const [facturesBadgeMap, setFacturesBadgeMap] = useState({});
  const [parkingMap, setParkingMap] = useState({});
  const [view, setView] = useState('tous');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingLitige, setEditingLitige] = useState(null);
  const [litigeInitial, setLitigeInitial] = useState(null); // pré-remplissage (ex. depuis Gestion Parking)
  const [selectedLitige, setSelectedLitige] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const isSuperAdmin = user?.role === 'super-admin';

  // Boutons d'action dans le header
  // Pré-remplissage demandé depuis une autre page (« + » d'une ligne de Gestion
  // Parking) : ouvre directement la création, consommé une seule fois.
  useEffect(() => {
    if (!prefill) return;
    setEditingLitige(null);
    setLitigeInitial(prefill);
    setShowModal(true);
    onPrefillConsumed();
  }, [prefill, onPrefillConsumed]);

  useEffect(() => {
    if (!setHeaderAction) return;
    const secondary = [
      { label: <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg> Import</>, onClick: () => setShowImport(true) },
    ];
    if (isSuperAdmin) {
      secondary.push({ label: '🤖 Analyse IA', onClick: () => handleAnalyse() });
    }
    setHeaderAction({
      primary: { label: '+ Nouveau litige', onClick: () => { setEditingLitige(null); setShowModal(true); } },
      secondary
    });
  }, [setHeaderAction, isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showAnalyseModal, setShowAnalyseModal] = useState(false);
  const [analyseLoading, setAnalyseLoading]     = useState(false);
  const [analyseResult, setAnalyseResult]       = useState('');
  const [filterConseilOnly, setFilterConseilOnly]   = useState(false);
  const [filterDimensions, setFilterDimensions]     = useState([]);
  const [filterConcession, setFilterConcession]     = useState('');
  const [archiveModalId, setArchiveModalId]       = useState(null);
  const [archiveComment, setArchiveComment]       = useState('');
  const [archiving, setArchiving]                 = useState(false);
  const [factureDetail, setFactureDetail]         = useState(null);

  useEffect(() => {
    loadData();
    loadReferentiels();
    loadStatuts();
    if (!isOperateur) loadConcessions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (view === 'archives') loadArchives();
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  // Charger les archives si on commence à chercher et qu'elles ne sont pas encore chargées
  useEffect(() => {
    if (searchTerm && archives.length === 0) loadArchives();
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await litigesApi.getAll({ tout: 1 });
      const litigesList = response.data.litiges;
      setLitiges(litigesList);
      // Chargement asynchrone des badges factures et parking pour tous les immat
      const immatList = [...new Set(litigesList.map(l => l.immatriculation).filter(Boolean))];
      if (immatList.length > 0) {
        api.post('/factures-services/by-immat-batch', { immat: immatList })
          .then(batchRes => setFacturesBadgeMap(batchRes.data.factures || {}))
          .catch(() => {});
        api.post('/parking/check-immat-batch', { immat: immatList })
          .then(batchRes => setParkingMap(batchRes.data.found || {}))
          .catch(() => {});
      }
    } catch (error) {
      console.error('Erreur chargement litiges:', error);
      alert('Erreur lors du chargement des litiges');
    } finally {
      setLoading(false);
    }
  };

  const loadArchives = async () => {
    try {
      const response = await litigesApi.getArchives({ search: searchTerm });
      setArchives(response.data.archives);
    } catch (error) {
      console.error('Erreur chargement archives:', error);
    }
  };

  const loadReferentiels = async () => {
    try {
      const [respRes, marqRes, modRes] = await Promise.all([
        referentielsApi.getResponsables('litiges'),
        referentielsApi.getMarques(),
        referentielsApi.getModeles()
      ]);
      setResponsables(respRes.data.responsables);
      setMarques(marqRes.data.marques);
      setModeles(modRes.data.modeles);
    } catch (error) {
      console.error('Erreur chargement référentiels:', error);
    }
  };

  const loadStatuts = async () => {
    try {
      const response = await statutsLitigesApi.getAll();
      setStatuts(response.data.statuts);
    } catch (error) {
      console.error('Erreur chargement statuts:', error);
    }
  };

  const loadConcessions = async () => {
    try {
      const response = await concessionsApi.getAll();
      setConcessions(response.data.concessions || []);
    } catch (error) {
      console.error('Erreur chargement concessions:', error);
    }
  };

  const handleUpdateStatutInline = async (id, newStatutId) => {
    try {
      await litigesApi.update(id, { statut_id: parseInt(newStatutId) });
      const s = statuts.find(s => s.id === parseInt(newStatutId));
      setLitiges(prev => prev.map(l => l.id === id
        ? { ...l, statut_id: parseInt(newStatutId), statut_nom: s?.nom, statut_couleur: s?.couleur }
        : l
      ));
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveLitige = async (litigeData, documents) => {
    try {
      let litigeId;
      if (editingLitige) {
        await litigesApi.update(editingLitige.id, litigeData);
        litigeId = editingLitige.id;
      } else {
        const response = await litigesApi.create(litigeData);
        litigeId = response.data.litige.id;
      }

      // Upload des documents en attente (blobs locaux)
      const localDocs = (documents || []).filter(d => d.isLocal && d.file);
      if (localDocs.length > 0) {
        const failedDocs = [];
        await Promise.all(localDocs.map(async (doc) => {
          try {
            if (doc.type === 'application/pdf') {
              // PDF → compression via backend (pdf-lib)
              const formData = new FormData();
              formData.append('file', doc.file, doc.name);
              await litigeDocsApi.uploadFile(litigeId, formData);
            } else {
              // Image → déjà compressée côté client, upload direct S3
              const presignRes = await litigeDocsApi.presign(litigeId, {
                filename: doc.name,
                contentType: doc.type,
              });
              await fetch(presignRes.data.uploadUrl, {
                method: 'PUT',
                body: doc.file,
                headers: { 'Content-Type': doc.type },
              });
              await litigeDocsApi.confirm(litigeId, {
                key: presignRes.data.key,
                filename: doc.name,
                contentType: doc.type,
                size: doc.file.size,
              });
            }
          } catch (err) {
            console.error('Erreur upload document:', doc.name, err);
            failedDocs.push(doc.name);
          }
        }));
        if (failedDocs.length > 0) {
          alert(`Le litige a été sauvegardé, mais ${failedDocs.length} document(s) n'ont pas pu être uploadé(s) :\n${failedDocs.join('\n')}\n\nVeuillez les ajouter manuellement depuis la fiche du litige.`);
        }
      }

      loadData();
      setShowModal(false);
      setEditingLitige(null);
    } catch (error) {
      console.error('Erreur sauvegarde litige:', error);
      alert(error.response?.data?.error || 'Erreur lors de la sauvegarde');
    }
  };

  const handleViewDetails = async (litige, editMode = false) => {
    try {
      const response = await litigesApi.getById(litige.id);
      setSelectedLitige({ ...response.data, _editMode: editMode });
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Erreur chargement détails:', error);
      alert('Erreur lors du chargement des détails');
    }
  };

  const handleArchive = (id) => {
    setArchiveModalId(id);
    setArchiveComment('');
  };

  const confirmArchive = async () => {
    if (!archiveComment.trim()) return;
    try {
      setArchiving(true);
      await litigesApi.addCommentaire(archiveModalId, `[Clôture] ${archiveComment}`);
      await litigesApi.archive(archiveModalId);
      setArchiveModalId(null);
      setArchiveComment('');
      loadData();
    } catch (error) {
      console.error('Erreur archivage:', error);
      alert('Erreur lors de l\'archivage');
    } finally {
      setArchiving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement ce litige ?')) return;
    try {
      await litigesApi.delete(id);
      loadData();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleAnalyse = async () => {
    setAnalyseLoading(true);
    setShowAnalyseModal(true);
    setAnalyseResult('');
    try {
      const res = await litigesApi.analyseAI();
      setAnalyseResult(res.data.analyse);
    } catch (err) {
      setAnalyseResult('❌ Erreur lors de l\'analyse : ' + (err.response?.data?.error || err.message));
    } finally {
      setAnalyseLoading(false);
    }
  };

  const matchConcession = (l) => {
    if (isSuperAdmin && filterConcession) {
      return l.concession_id === parseInt(filterConcession, 10);
    }
    if (!user?.concession_id) return true;
    return l.concession_id === user.concession_id;
  };

  const matchSearch = (l) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      l.nom?.toLowerCase().includes(s) ||
      l.prenom?.toLowerCase().includes(s) ||
      l.telephone?.includes(searchTerm) ||
      l.immatriculation?.toLowerCase().includes(s)
    );
  };

  const matchDimensions = (l) => {
    if (filterDimensions.length === 0) return true;
    const dims = l.dimensions_actives || l.dimensions || [];
    return filterDimensions.some(d => dims.includes(d));
  };

  const filteredTous     = litiges.filter(l => matchSearch(l) && matchConcession(l) && (!filterConseilOnly || l.chez_conseil) && matchDimensions(l));
  const filteredArchives = archives.filter(a => matchSearch(a) && matchConcession(a));

  const filteredLitiges  = filteredTous;
  const countTous = litiges.length;

  return (
    <div className="page-container">
      <div className="page-tabs">
        <button
          className={view === 'tous' ? 'active' : ''}
          onClick={() => setView('tous')}
        >
          📋 Litiges en cours
          {!loading && countTous > 0 && <span className="tab-count">{countTous}</span>}
        </button>
        <button
          className={view === 'archives' ? 'active' : ''}
          onClick={() => setView('archives')}
        >
          🗄️ Archives
        </button>
      </div>

      <div className="page-toolbar">
        <input
          type="text"
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button
          className={`page-select${filterConseilOnly ? ' active-filter' : ''}`}
          onClick={() => setFilterConseilOnly(v => !v)}
        >
          ⚖️ Conseil
        </button>
        {isSuperAdmin && (
          <select
            className={`page-select${filterConcession ? ' active-filter' : ''}`}
            value={filterConcession}
            onChange={e => setFilterConcession(e.target.value)}
          >
            <option value="">Toutes les concessions</option>
            {concessions.map(c => (
              <option key={c.id} value={c.id}>{c.nom}</option>
            ))}
          </select>
        )}
      </div>

      <div className="filter-chips-bar">
        {DIMENSIONS.map(dim => {
          const active = filterDimensions.includes(dim.key);
          const allLitiges = view === 'archives' ? filteredArchives : litiges.filter(l => matchSearch(l) && matchConcession(l) && (!filterConseilOnly || l.chez_conseil));
          const count = allLitiges.filter(l => (l.dimensions_actives || l.dimensions || []).includes(dim.key)).length;
          return (
            <button
              key={dim.key}
              className={`filter-chip ${active ? 'chip-active' : 'chip-default'}`}
              style={active ? { borderColor: dim.couleur, backgroundColor: dim.couleur + '22', color: texteTheme(dim.couleur) } : {}}
              onClick={() => setFilterDimensions(prev =>
                prev.includes(dim.key) ? prev.filter(d => d !== dim.key) : [...prev, dim.key]
              )}
            >
              {dim.label}
              <span className="chip-count">{count}</span>
            </button>
          );
        })}
      </div>

      {loading && <div className="loading">Chargement...</div>}

      {/* ── Mode recherche : 2 sections simultanées ── */}
      {!loading && searchTerm && (<>
        <div className="search-section-hd">📋 Litiges en cours <span className="search-section-count">{filteredTous.length}</span></div>
        <LitigesView
          litiges={filteredTous} statuts={statuts} isOperateur={isOperateur} isSuperAdmin={isSuperAdmin}
          facturesBadgeMap={facturesBadgeMap} parkingMap={parkingMap} onShowFacture={setFactureDetail}
          emptyMessage="Aucun résultat dans «&nbsp;Litiges en cours&nbsp;»"
          onEdit={(litige) => { setEditingLitige(litige); setShowModal(true); }}
          onViewDetails={handleViewDetails} onArchive={handleArchive}
          onDelete={handleDelete} onUpdateStatut={handleUpdateStatutInline} onOpenVehicule={onOpenVehicule}
          editMode={editMode} onOuvrirDossier={ouvrirDossier}
        />
        <div className="search-section-hd">🗄️ Archives <span className="search-section-count">{filteredArchives.length}</span></div>
        <ArchivesView archives={filteredArchives} isOperateur={isOperateur} onOpenVehicule={onOpenVehicule} />
      </>)}

      {/* ── Mode normal : onglet actif ── */}
      {!loading && !searchTerm && view === 'tous' && (
        <LitigesView
          litiges={filteredLitiges}
          statuts={statuts}
          isOperateur={isOperateur}
          isSuperAdmin={isSuperAdmin}
          emptyMessage="Aucun litige en cours"
          onEdit={(litige) => {
            setEditingLitige(litige);
            setShowModal(true);
          }}
          onViewDetails={handleViewDetails}
          onArchive={handleArchive}
          onDelete={handleDelete}
          onUpdateStatut={handleUpdateStatutInline}
          onOpenVehicule={onOpenVehicule}
          editMode={editMode}
          facturesBadgeMap={facturesBadgeMap} parkingMap={parkingMap} onShowFacture={setFactureDetail}
          onOuvrirDossier={ouvrirDossier}
        />
      )}

      {!loading && !searchTerm && view === 'archives' && (
        <ArchivesView archives={filteredArchives} isOperateur={isOperateur} onOpenVehicule={onOpenVehicule} />
      )}

      {showModal && (
        <LitigeModal
          litige={editingLitige}
          initial={litigeInitial}
          statuts={statuts}
          responsables={responsables}
          marques={marques}
          modeles={modeles}
          concessions={concessions}
          isOperateur={isOperateur}
          user={user}
          onSave={handleSaveLitige}
          onClose={() => {
            setShowModal(false);
            setEditingLitige(null);
            setLitigeInitial(null);
          }}
        />
      )}

      {showDetailsModal && selectedLitige && (
        <LitigeDetailsModal
          litige={selectedLitige}
          user={user}
          responsables={responsables}
          concessions={concessions}
          isOperateur={isOperateur}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedLitige(null);
          }}
          onCommentAdded={loadData}
        />
      )}

      {showImport && (
        <ImportModal
          onClose={() => setShowImport(false)}
          onDone={loadData}
        />
      )}

      {factureDetail && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={() => setFactureDetail(null)}
        >
          <div
            style={{ background: 'var(--bg-surface)', borderRadius: '10px', width: '100%', maxWidth: '780px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border-default)' }}>
              <div>
                <span style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>🧾 {factureDetail.num_facture}</span>
                <span style={{ marginLeft: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                  {(() => { const d = factureDetail.date_facture; if (!d) return '—'; const [y,m,day] = d.split('T')[0].split('-').map(Number); return new Date(y, m-1, day).toLocaleDateString('fr-FR'); })()} · OR : {factureDetail.num_or || '—'}
                </span>
              </div>
              <button onClick={() => setFactureDetail(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'var(--text-secondary)' }}>✕</button>
            </div>
            <div style={{ padding: '16px 20px', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                {[
                  { label: 'Client',           value: factureDetail.nom_client },
                  { label: 'Immatriculation',  value: factureDetail.immat },
                  { label: 'VIN',              value: factureDetail.vin },
                  { label: 'Marque',           value: factureDetail.marque },
                  { label: 'Société',          value: factureDetail.societe },
                  { label: 'Département',      value: factureDetail.department },
                  { label: 'Réceptionnaire',   value: factureDetail.receptionnaire },
                  { label: 'Ouverture OR',     value: (() => { const d = factureDetail.date_ouverture; if (!d) return '—'; const [y,m,day] = d.split('T')[0].split('-').map(Number); return new Date(y, m-1, day).toLocaleDateString('fr-FR'); })() },
                  { label: 'Date facture',     value: (() => { const d = factureDetail.date_facture; if (!d) return '—'; const [y,m,day] = d.split('T')[0].split('-').map(Number); return new Date(y, m-1, day).toLocaleDateString('fr-FR'); })() },
                  { label: 'Montant HT total', value: new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(factureDetail.montant_ht), highlight: true },
                ].map(item => (
                  <div key={item.label} style={{ background: 'var(--bg-muted)', borderRadius: '6px', padding: '10px 12px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '3px' }}>{item.label}</div>
                    <div style={{ fontWeight: item.highlight ? 700 : 500, color: item.highlight ? 'var(--color-success)' : 'var(--text-primary)', fontSize: item.highlight ? '15px' : '13px' }}>
                      {item.value || '—'}
                    </div>
                  </div>
                ))}
              </div>
              {factureDetail.lignes_json && factureDetail.lignes_json.length > 0 && (
                <>
                  <h3 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>
                    Lignes ({factureDetail.lignes_json.length})
                  </h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-muted)' }}>
                        {['Prestation', 'Désignation', 'Réf.', 'Qté', 'Montant HT'].map(h => (
                          <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-primary)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {factureDetail.lignes_json.map((l, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid light-dark(#f3f4f6, var(--border-default))' }}>
                          <td style={{ padding: '6px 10px' }}>
                            <span style={{
                              background: l.prestation === 'M.O.' ? 'light-dark(#fef3c7, var(--color-warning-light))' : 'light-dark(#eff6ff, var(--color-info-light))',
                              color:      l.prestation === 'M.O.' ? 'light-dark(#92400e, var(--color-warning-dark))' : 'light-dark(#1d4ed8, var(--color-info-dark))',
                              padding: '1px 6px', borderRadius: '4px', fontWeight: 600, fontSize: '11px',
                            }}>{l.prestation || '—'}</span>
                          </td>
                          <td style={{ padding: '6px 10px', color: 'var(--text-primary)' }}>{l.designation || '—'}</td>
                          <td style={{ padding: '6px 10px', fontFamily: 'monospace', color: 'var(--text-secondary)', fontSize: '11px' }}>{l.ref || '—'}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right' }}>{l.qte != null ? l.qte : '—'}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: parseFloat(l.montant_ht) < 0 ? 'var(--color-error)' : 'var(--color-success)' }}>
                            {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(l.montant_ht)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: 'var(--bg-muted)', borderTop: '2px solid var(--border-default)' }}>
                        <td colSpan={4} style={{ padding: '8px 10px', fontWeight: 700, textAlign: 'right', fontSize: '13px' }}>Total HT</td>
                        <td style={{ padding: '8px 10px', fontWeight: 700, fontSize: '14px', textAlign: 'right', color: parseFloat(factureDetail.montant_ht) < 0 ? 'var(--color-error)' : 'var(--color-success)' }}>
                          {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(factureDetail.montant_ht)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {archiveModalId && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✅ Archiver le litige</h2>
              <button className="modal-close" onClick={() => { if (!archiving) setArchiveModalId(null); }}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 12, color: 'var(--text-secondary)' }}>Comment ce litige s'est-il soldé ?</p>
              <textarea
                autoFocus
                rows={4}
                style={{ width: '100%', resize: 'vertical', padding: 10, borderRadius: 8, border: '1px solid var(--border-default)', fontFamily: 'inherit', fontSize: 14 }}
                placeholder="Ex : Accord trouvé avec le client, geste commercial de 200€…"
                value={archiveComment}
                onChange={e => setArchiveComment(e.target.value)}
              />
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setArchiveModalId(null)} disabled={archiving}>Annuler</button>
              <button className="btn-primary" onClick={confirmArchive} disabled={archiving || !archiveComment.trim()}>
                {archiving ? 'Archivage…' : 'Archiver'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAnalyseModal && (
        <div className="modal-overlay">
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🤖 Analyse IA — Rapport de pilotage Litiges</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {analyseResult && !analyseLoading && (
                  <button className="btn-secondary"
                    onClick={() => navigator.clipboard.writeText(analyseResult)}>
                    📋 Copier
                  </button>
                )}
                <button className="modal-close" onClick={() => setShowAnalyseModal(false)}>✕</button>
              </div>
            </div>
            <div className="modal-body">
              {analyseLoading ? (
                <div className="analyse-loading">
                  <div className="analyse-spinner" />
                  <p>Analyse en cours… Cette opération peut prendre 15 à 30 secondes.</p>
                </div>
              ) : (
                <pre className="analyse-result">{analyseResult}</pre>
              )}
            </div>
          </div>
        </div>
      )}
      {modaleDossier}
    </div>
  );
}

// Composant tableau litiges actifs
function LitigesView({ litiges, statuts, isOperateur, isSuperAdmin, emptyMessage, onEdit, onViewDetails, onArchive, onDelete, onUpdateStatut, onOpenVehicule = () => {}, grouped = false, editMode = false, facturesBadgeMap = {}, parkingMap = {}, onShowFacture = () => {}, onOuvrirDossier = () => {} }) {
  const showConcession = !isOperateur;
  const colSpan = (showConcession ? 11 : 10);
  const todayStr = new Date().toISOString().split('T')[0];
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir]     = useState('asc');

  const getD = (l) => l.date_prochaine_action ? l.date_prochaine_action.split('T')[0] : null;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIndicator = (field) => {
    if (sortField !== field) return <span style={{ color: '#d1d5db', marginLeft: '4px' }}>⇅</span>;
    return <span style={{ marginLeft: '4px' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const sortedRows = [...litiges].sort((a, b) => {
    if (!sortField) return 0;
    let va, vb;
    if (sortField === 'immatriculation') { va = a.immatriculation || ''; vb = b.immatriculation || ''; }
    else if (sortField === 'vehicule')   { va = `${a.marque||''} ${a.modele||''}`; vb = `${b.marque||''} ${b.modele||''}`; }
    else if (sortField === 'client')     { va = `${a.nom||''} ${a.prenom||''}`; vb = `${b.nom||''} ${b.prenom||''}`; }
    else if (sortField === 'statut')     { va = (a.dimensions_actives||[]).length; vb = (b.dimensions_actives||[]).length; }
    else if (sortField === 'responsable'){ va = `${a.responsable_nom||''} ${a.responsable_prenom||''}`; vb = `${b.responsable_nom||''} ${b.responsable_prenom||''}`; }
    else if (sortField === 'concession') { va = a.concession_nom || ''; vb = b.concession_nom || ''; }
    else if (sortField === 'date_prochaine_action') { va = a.date_prochaine_action || ''; vb = b.date_prochaine_action || ''; }
    else if (sortField === 'derniere_facture_date') {
      const fa = facturesBadgeMap[(a.immatriculation || '').toUpperCase()];
      const fb = facturesBadgeMap[(b.immatriculation || '').toUpperCase()];
      va = fa?.date_facture || ''; vb = fb?.date_facture || '';
    }
    else return 0;
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ?  1 : -1;
    return 0;
  });

  const thStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

  return (
    <div className="table-container">
      <table>
        <thead>
          <tr>
            <th style={thStyle} onClick={() => handleSort('immatriculation')}>Immat{sortIndicator('immatriculation')}</th>
            <th style={{ textAlign: 'center', width: '1%', padding: '4px' }} title="Gestion Parking">🅿️</th>
            <th style={thStyle} onClick={() => handleSort('vehicule')}>Véhicule{sortIndicator('vehicule')}</th>
            <th style={thStyle} onClick={() => handleSort('client')}>Client{sortIndicator('client')}</th>
            <th style={thStyle} onClick={() => handleSort('statut')}>Statut{sortIndicator('statut')}</th>
            <th style={thStyle} onClick={() => handleSort('responsable')}>Responsable{sortIndicator('responsable')}</th>
            {showConcession && <th style={thStyle} onClick={() => handleSort('concession')}>Concession{sortIndicator('concession')}</th>}
            {/* Rapprochement par IMMATRICULATION : un litige ne porte pas de
                numero d'OR, contrairement aux autres ecrans. */}
            <th style={thStyle} onClick={() => handleSort('date_planification')}>Planification{sortIndicator('date_planification')}</th>
            <th style={thStyle} onClick={() => handleSort('date_prochaine_action')}>Prochaine action{sortIndicator('date_prochaine_action')}</th>
            <th style={thStyle} onClick={() => handleSort('derniere_facture_date')}>Date facture{sortIndicator('derniere_facture_date')}</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedRows.length === 0 ? (
            <tr>
              <td colSpan={colSpan} style={{ textAlign: 'center', padding: '2rem', color: 'light-dark(#6c757d, var(--text-secondary))' }}>
                {emptyMessage || 'Aucun litige'}
              </td>
            </tr>
          ) : (
            sortedRows.map((l) => {
              const dateStr  = getD(l);
              const isLate   = dateStr && dateStr < todayStr;
              const isToday  = dateStr && dateStr === todayStr;
              const isFuture = dateStr && dateStr > todayStr;

              return (
                <tr key={l.id} style={{ cursor: 'pointer' }} onClick={() => onViewDetails(l)}>
                  <td>
                    {l.immatriculation
                      ? <span
                          className="plaque-badge plaque-badge-link"
                          onClick={e => { e.stopPropagation(); onOpenVehicule(l.immatriculation); }}
                        >{l.immatriculation}</span>
                      : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td style={{ textAlign: 'center', padding: '4px' }}>
                    {l.immatriculation && parkingMap[(l.immatriculation || '').replace(/[-\s]/g, '').toUpperCase()] ? (
                      <span
                        style={{ cursor: 'pointer', fontSize: '16px' }}
                        title="Véhicule au parking"
                        onClick={e => { e.stopPropagation(); onOpenVehicule(l.immatriculation); }}
                      >🅿️</span>
                    ) : null}
                  </td>
                  <td>
                    {(l.marque || l.modele)
                      ? <>
                          <div style={{ fontWeight: 600 }}>{l.marque}</div>
                          {l.modele && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px', textTransform: 'uppercase' }}>{l.modele}</div>}
                        </>
                      : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{l.nom}</div>
                    {l.prenom && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px' }}>{l.prenom}</div>}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <div className="dimensions-badges">
                      {DIMENSIONS.map(dim => {
                        const actif = (l.dimensions_actives || []).includes(dim.key);
                        return (
                          <span
                            key={dim.key}
                            className={`dim-badge ${actif ? 'dim-badge-actif' : 'dim-badge-inactif'}`}
                            style={actif ? { background: dim.couleur, color: '#fff' } : {}}
                            title={dim.label}
                          >
                            {dim.abbr}
                          </span>
                        );
                      })}
                    </div>
                    {l.chez_conseil && (
                      <span className="badge-conseil-readonly">⚖️ Conseil</span>
                    )}
                  </td>
                  <td>
                    <div>{l.responsable_nom}</div>
                    {l.responsable_prenom && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px' }}>{l.responsable_prenom}</div>}
                  </td>
                  {showConcession && <td>{l.concession_nom || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>}
                  <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                    <PlanificationCell dossier={l} onOuvrir={onOuvrirDossier} />
                  </td>
                  <td>
                    {dateStr ? (
                      <span style={{
                        display: 'inline-block',
                        padding: '3px 8px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: isLate ? 'light-dark(#fee2e2, var(--color-error-light))' : isToday ? 'light-dark(#fef9c3, var(--color-warning-light))' : 'light-dark(#e0f2fe, var(--color-info-light))',
                        color:      isLate ? 'light-dark(#b91c1c, var(--color-error-dark))' : isToday ? 'light-dark(#854d0e, var(--color-warning-dark))' : 'light-dark(#0369a1, var(--color-info-dark))',
                      }}>
                        {isLate && '⚠️ '}
                        {isToday && '📌 '}
                        {isFuture && '📅 '}
                        {new Date(dateStr + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>—</span>
                    )}
                  </td>
                  {(() => {
                    const factureData = facturesBadgeMap[(l.immatriculation || '').toUpperCase()];
                    const dateFacture = factureData?.date_facture;
                    return (
                      <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        {dateFacture ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                            <span>{new Date(dateFacture).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>
                            <span className="alert-badge badge-facture badge-facture-clickable" title="Voir la dernière facture atelier" style={{ cursor: 'pointer' }} onClick={() => onShowFacture(factureData)}>FAC</span>
                          </div>
                        ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                    );
                  })()}
                  <td onClick={e => e.stopPropagation()}>
                    <div className="action-buttons">
                      <button onClick={(e) => { e.stopPropagation(); onViewDetails(l, true); }} className="btn-icon" title="Modifier">✏️</button>
                      <button onClick={() => onArchive(l.id)} className="btn-icon" title="Archiver">✅</button>
                      {editMode && !isOperateur && (
                        <button onClick={() => onDelete(l.id)} className="btn-icon delete" title="Supprimer">🗑️</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

// Composant tableau archives
function ArchivesView({ archives, isOperateur, onOpenVehicule = () => {} }) {
  const colSpan = isOperateur ? 6 : 7;
  return (
    <div className="table-container">
      <table>
        <thead>
          <tr>
            <th>Date archivage</th>
            <th>Véhicule</th>
            <th>Client</th>
            <th>Statut final</th>
            <th>Responsable</th>
            {!isOperateur && <th>Concession</th>}
            <th>⏱️ Durée</th>
          </tr>
        </thead>
        <tbody>
          {archives.length === 0 ? (
            <tr>
              <td colSpan={colSpan} style={{ textAlign: 'center', padding: '2rem', color: 'light-dark(#6c757d, var(--text-secondary))' }}>
                Aucune archive
              </td>
            </tr>
          ) : (
            archives.map(a => {
              const created = new Date(a.created_at);
              const archived = new Date(a.date_archivage);
              const ms = archived - created;
              const days = Math.floor(ms / (1000 * 60 * 60 * 24));
              const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

              return (
                <tr key={a.id}>
                  <td>{new Date(a.date_archivage).toLocaleDateString('fr-FR')}</td>
                  <td>
                    <div style={{ fontWeight: '600' }}>{a.marque} {a.modele}</div>
                    {a.immatriculation && (
                      <div
                        onClick={e => { e.stopPropagation(); onOpenVehicule(a.immatriculation); }}
                        style={{ marginTop: '2px', cursor: 'pointer' }}
                      >
                        <span className="plaque-badge plaque-badge-link">{a.immatriculation}</span>
                      </div>
                    )}
                  </td>
                  <td className="font-bold">{a.nom} {a.prenom}</td>
                  <td>{a.statut_nom}</td>
                  <td>{a.responsable_nom}</td>
                  {!isOperateur && <td>{a.concession_nom || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>}
                  <td>
                    <span style={{
                      fontSize: '11px',
                      background: days > 7 ? 'light-dark(#fee2e2, var(--color-error-light))' : 'light-dark(#dcfce7, var(--color-success-light))',
                      color: days > 7 ? 'light-dark(#991b1b, var(--color-error-dark))' : 'light-dark(#166534, var(--color-success-dark))',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontWeight: '600'
                    }}>
                      {days}j {hours}h
                    </span>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

// Modal création/modification litige
function LitigeModal({ litige, initial = null, statuts, responsables, marques, modeles, concessions, isOperateur, user, onSave, onClose }) {
  const [formData, setFormData] = useState(litige ? {
    ...litige,
    dimensions: litige.dimensions || litige.dimensions_actives || []
  } : {
    immatriculation: '',
    nom: '',
    prenom: '',
    telephone: '',
    email: '',
    marque: '',
    modele: '',
    dimensions: [],
    responsable_id: '',
    concession_id: '',
    date_prochaine_action: '',
    date_reception: '',
    date_premier_contact: '',
    date_cloture: '',
    commentaire_initial: '',
    ...(initial ? {
      immatriculation: initial.immatriculation || '',
      nom: initial.nom || '',
    } : {}),
  });
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    if (!isOperateur && !litige && concessions.length > 0) {
      // Pré-rempli depuis le parking : la concession de la ligne prime sur la dernière utilisée.
      const depuisLigne = initial?.concession_nom && concessions.find(c => c.nom === initial.concession_nom);
      if (depuisLigne) { setFormData(prev => ({ ...prev, concession_id: depuisLigne.id })); return; }
      const last = localStorage.getItem('last_concession_id_litige');
      if (last) setFormData(prev => ({ ...prev, concession_id: parseInt(last) }));
    }
  }, [isOperateur, litige, concessions, initial]);

  // Marque pré-remplie : le parking la stocke en majuscules (« NISSAN »), le
  // référentiel en casse normale (« Nissan ») ; inconnue ⇒ laissée vide.
  useEffect(() => {
    if (litige || !initial?.marque || marques.length === 0) return;
    const m = marques.find(x => (x.nom || '').toUpperCase() === initial.marque.toUpperCase());
    if (m) setFormData(prev => (prev.marque ? prev : { ...prev, marque: m.nom }));
  }, [litige, initial, marques]);

  const handleConcessionChange = (id) => {
    const respStillValid = responsables.find(r => r.id === parseInt(formData.responsable_id) && r.concession_id === id);
    setFormData(prev => ({ ...prev, concession_id: id, responsable_id: respStillValid ? prev.responsable_id : '' }));
    if (!isOperateur) localStorage.setItem('last_concession_id_litige', id);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData, documents);
  };

  const respOptions = responsables
    .filter(r => !formData.concession_id || r.concession_id === parseInt(formData.concession_id))
    .map(r => {
      const nom = `${r.nom} ${r.prenom || ''}`.trim();
      return { value: String(r.id), display: nom, label: r.fonction ? `${nom} — ${r.fonction}` : nom, search: `${nom} ${r.fonction || ''}`.trim() };
    });
  // Injecter le responsable assigné s'il n'est pas dans la liste filtrée
  if (litige?.responsable_id && !respOptions.find(o => o.value === String(litige.responsable_id))) {
    const nom = `${litige.responsable_nom || ''} ${litige.responsable_prenom || ''}`.trim();
    respOptions.unshift({ value: String(litige.responsable_id), display: nom, label: nom, search: nom });
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content leads-modal-content" onClick={e => e.stopPropagation()}>

        {/* ── Header : titre + concession pills + fermer ────────────── */}
        <div className="modal-header">
          <h2>{litige ? 'Modifier le litige' : 'Nouveau litige'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {!isOperateur && (
              <div className={`concession-picker${!formData.concession_id ? ' concession-picker--required' : ''}`}>
                {concessions.map(c => (
                  <button key={c.id} type="button"
                    className={`concession-btn${formData.concession_id === c.id ? ' selected' : ''}`}
                    onClick={() => handleConcessionChange(c.id)}
                    title={c.nom}
                  >
                    {CONCESSION_LABELS[c.code] || c.nom.split(' ')[0]}
                  </button>
                ))}
              </div>
            )}
            <button type="button" onClick={onClose} className="modal-close">✕</button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="leads-modal-form">
          <div className="form-scroll-body">

            {/* ── Section CLIENT ──────────────────────────────────────── */}
            <div className="form-section">
              <div className="form-section-hd">👤 Client</div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Nom</label>
                  <input type="text" value={formData.nom || ''}
                    onChange={e => setFormData({ ...formData, nom: e.target.value.toUpperCase() })}
                    placeholder="DUPONT" />
                </div>
                <div className="form-group">
                  <label>Prénom</label>
                  <input type="text" value={formData.prenom || ''}
                    onChange={e => {
                      const v = e.target.value;
                      setFormData({ ...formData, prenom: v ? v.charAt(0).toUpperCase() + v.slice(1).toLowerCase() : '' });
                    }}
                    placeholder="Jean" />
                </div>
                <div className="form-group">
                  <label>Téléphone</label>
                  <input type="tel" value={formData.telephone || ''}
                    onChange={e => {
                      const cleaned = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setFormData({ ...formData, telephone: cleaned });
                    }}
                    maxLength={10} placeholder="0612345678" />
                  {formData.telephone?.length === 10 && (
                    <small style={{ color: 'light-dark(var(--color-success), var(--color-success-dark))', fontWeight: '600' }}>✓ Numéro valide</small>
                  )}
                  {formData.telephone?.length > 0 && formData.telephone.length < 10 && (
                    <small style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))', fontWeight: '600' }}>
                      ✗ 10 chiffres requis ({formData.telephone.length}/10)
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="email" value={formData.email || ''}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                      placeholder="client@example.com" style={{ flex: 1 }} />
                    {formData.email && (
                      <span style={{ fontSize: '18px', flexShrink: 0 }}>
                        {/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) ? '✅' : '❌'}
                      </span>
                    )}
                  </div>
                  <div className="email-domain-btns">
                    {['@gmail.com', '@outlook.fr', '@hotmail.fr'].map(d => (
                      <button key={d} type="button" className="email-domain-btn"
                        onClick={() => setFormData({ ...formData, email: (formData.email || '').split('@')[0] + d })}>
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Section VÉHICULE ────────────────────────────────────── */}
            <div className="form-section">
              <div className="form-section-hd">🚗 Véhicule</div>
              <div className="form-grid">
                <div className="form-group full">
                  <label>Marque</label>
                  <div className="brand-picker">
                    {marques.map(m => (
                      <button key={m.id} type="button"
                        className={`brand-btn${formData.marque === m.nom ? ' selected' : ''}`}
                        onClick={() => setFormData({ ...formData, marque: m.nom, modele: '' })}>
                        <BrandLogo nom={m.nom} />
                        {m.nom}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-row-3">
                  <div className="form-group">
                    <label>Modèle</label>
                    <input type="text" list="litige-modeles-list" value={formData.modele || ''}
                      onChange={e => setFormData({ ...formData, modele: e.target.value })}
                      placeholder="Sélectionnez d'abord une marque" disabled={!formData.marque} />
                    <datalist id="litige-modeles-list">
                      {modeles.filter(m => m.marque_nom === formData.marque).map(m => (
                        <option key={m.id} value={m.nom} />
                      ))}
                    </datalist>
                  </div>
                  <div className="form-group">
                    <label>Immatriculation</label>
                    <input type="text" value={formData.immatriculation || ''}
                      onChange={e => setFormData({ ...formData, immatriculation: e.target.value.replace(/[\s-]/g, '').toUpperCase() })}
                      placeholder="AA123BB" />
                  </div>
                </div>
              </div>
            </div>

            {/* ── Section LITIGE ──────────────────────────────────────── */}
            <div className="form-section">
              <div className="form-section-hd">⚖️ Litige</div>
              <div className="form-grid">
                <div className="form-row-3">
                  <div className="form-group">
                    <label>Date de réception</label>
                    <input type="date" value={formData.date_reception || ''}
                      onChange={e => setFormData({ ...formData, date_reception: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Date de 1er contact</label>
                    <input type="date" value={formData.date_premier_contact || ''}
                      onChange={e => setFormData({ ...formData, date_premier_contact: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Date de clôture</label>
                    <input type="date" value={formData.date_cloture || ''}
                      onChange={e => setFormData({ ...formData, date_cloture: e.target.value })} />
                  </div>
                </div>
                <div className="form-group full">
                  <label>Dimensions</label>
                  <div className="sujet-picker">
                    {DIMENSIONS.map(dim => {
                      const actif = (formData.dimensions || []).includes(dim.key);
                      return (
                        <button key={dim.key} type="button"
                          className={`sujet-btn${actif ? ' selected' : ''}`}
                          style={actif ? { borderColor: dim.couleur, background: dim.couleur + '22', color: texteTheme(dim.couleur) } : {}}
                          onClick={() => {
                            const next = actif
                              ? (formData.dimensions || []).filter(d => d !== dim.key)
                              : [...(formData.dimensions || []), dim.key];
                            setFormData({ ...formData, dimensions: next });
                          }}
                        >
                          {dim.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="form-group full">
                  <label className="conseil-checkbox-label">
                    <input type="checkbox"
                      checked={!!formData.chez_conseil}
                      onChange={e => setFormData({ ...formData, chez_conseil: e.target.checked })}
                    />
                    ⚖️ Transmis à notre conseil (avocat)
                  </label>
                </div>
                <div className="form-group">
                  <label>Responsable</label>
                  <SearchableInput
                    value={String(formData.responsable_id || '')}
                    onChange={val => setFormData({ ...formData, responsable_id: val ? parseInt(val) : '' })}
                    options={respOptions}
                    placeholder="Rechercher un responsable..."
                  />
                </div>
                <div className="form-group">
                  <label>📅 Date de prochaine action</label>
                  <input type="date"
                    value={formData.date_prochaine_action ? formData.date_prochaine_action.split('T')[0] : ''}
                    onChange={e => setFormData({ ...formData, date_prochaine_action: e.target.value || null })} />
                </div>
                {!litige && (
                  <div className="form-group full">
                    <label>Commentaire initial</label>
                    <textarea value={formData.commentaire_initial || ''}
                      onChange={e => setFormData({ ...formData, commentaire_initial: e.target.value })}
                      rows="3" placeholder="Décrivez le litige..." />
                  </div>
                )}
              </div>
            </div>

          </div>{/* /form-scroll-body */}

          <DocumentSection documents={documents} setDocuments={setDocuments} />

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn-primary">{litige ? 'Modifier' : 'Créer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// CommentDocThumb, DocumentViewer, PdfThumbnail importés depuis ./shared/DocumentViewer

// Modal détails avec commentaires
export function LitigeDetailsModal({ litige, user, onClose, onCommentAdded, responsables = [], concessions = [], isOperateur = false }) {
  const [activeTab, setActiveTab] = useState('apercu');
  const [newComment, setNewComment] = useState('');
  const [newCommentDimension, setNewCommentDimension] = useState('amiable');
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  // Doc attaché au nouveau commentaire
  const [pendingDoc, setPendingDoc] = useState(null); // { file, url, name }
  const [docDragging, setDocDragging] = useState(false);
  const commentDocInputRef = useRef(null);
  const [dateAction, setDateAction] = useState(
    litige.litige.date_prochaine_action ? litige.litige.date_prochaine_action.split('T')[0] : ''
  );
  const [savingDate, setSavingDate] = useState(false);
  const isAdmin = user?.role === 'super-admin' || user?.role === 'admin';
  const isSuperAdmin = user?.role === 'super-admin';
  const [dossierAnalyse, setDossierAnalyse] = useState(null);
  const [dossierAnalyseLoading, setDossierAnalyseLoading] = useState(false);
  const [localDimensions, setLocalDimensions] = useState(
    litige.litige?.dimensions || litige.litige?.dimensions_actives || []
  );
  const [isEditing, setIsEditing] = useState(litige._editMode || false);
  const [editFormData, setEditFormData] = useState({
    nom:             litige.litige.nom             || '',
    prenom:          litige.litige.prenom          || '',
    telephone:       litige.litige.telephone       || '',
    email:           litige.litige.email           || '',
    immatriculation: litige.litige.immatriculation || '',
    marque:          litige.litige.marque          || '',
    modele:          litige.litige.modele          || '',
    responsable_id:  litige.litige.responsable_id  || '',
    concession_id:   litige.litige.concession_id   || '',
  });
  const [dateCreation, setDateCreation] = useState(
    litige.litige.created_at ? litige.litige.created_at.split('T')[0] : ''
  );
  const [savingDateCreation, setSavingDateCreation] = useState(false);
  const [dateReception, setDateReception] = useState(
    litige.litige.date_reception ? litige.litige.date_reception.split('T')[0] : ''
  );
  const [savingDateReception, setSavingDateReception] = useState(false);
  const [datePremierContact, setDatePremierContact] = useState(
    litige.litige.date_premier_contact ? litige.litige.date_premier_contact.split('T')[0] : ''
  );
  const [savingDatePremierContact, setSavingDatePremierContact] = useState(false);
  const [dateCloture, setDateCloture] = useState(
    litige.litige.date_cloture ? litige.litige.date_cloture.split('T')[0] : ''
  );
  const [savingDateCloture, setSavingDateCloture] = useState(false);

  useEffect(() => {
    loadDocuments();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDocuments = async () => {
    try {
      setDocsLoading(true);
      const res = await litigeDocsApi.getAll(litige.litige.id);
      setDocuments(res.data.documents.map(d => normalizeDoc({ ...d, isLocal: false })));
    } catch (err) {
      console.error('Erreur chargement documents:', err);
    } finally {
      setDocsLoading(false);
    }
  };

  const handleSaveDate = async () => {
    setSavingDate(true);
    try {
      await litigesApi.update(litige.litige.id, { date_prochaine_action: dateAction || null });
      onCommentAdded(); // recharge la liste
    } catch (err) {
      alert('Erreur lors de la sauvegarde de la date');
    } finally {
      setSavingDate(false);
    }
  };

  const handleSaveDateCreation = async () => {
    setSavingDateCreation(true);
    try {
      await litigesApi.update(litige.litige.id, { created_at: dateCreation || null });
      onCommentAdded();
    } catch (err) {
      alert('Erreur lors de la sauvegarde de la date de création');
    } finally {
      setSavingDateCreation(false);
    }
  };

  const handleSaveDateReception = async () => {
    setSavingDateReception(true);
    try {
      await litigesApi.update(litige.litige.id, { date_reception: dateReception || null });
      onCommentAdded();
    } catch (err) {
      alert('Erreur lors de la sauvegarde de la date de réception');
    } finally {
      setSavingDateReception(false);
    }
  };

  const handleSaveDatePremierContact = async () => {
    setSavingDatePremierContact(true);
    try {
      await litigesApi.update(litige.litige.id, { date_premier_contact: datePremierContact || null });
      onCommentAdded();
    } catch (err) {
      alert('Erreur lors de la sauvegarde de la date de 1er contact');
    } finally {
      setSavingDatePremierContact(false);
    }
  };

  const handleSaveDateCloture = async () => {
    setSavingDateCloture(true);
    try {
      await litigesApi.update(litige.litige.id, { date_cloture: dateCloture || null });
      onCommentAdded();
    } catch (err) {
      alert('Erreur lors de la sauvegarde de la date de clôture');
    } finally {
      setSavingDateCloture(false);
    }
  };

  const handleToggleDimension = async (key) => {
    const prev = localDimensions;
    const next = localDimensions.includes(key)
      ? localDimensions.filter(d => d !== key)
      : [...localDimensions, key];
    setLocalDimensions(next);
    try {
      await litigesApi.update(litige.litige.id, { dimensions: next });
      onCommentAdded();
    } catch (err) {
      console.error('Erreur mise à jour dimensions:', err);
      setLocalDimensions(prev);
      alert('Erreur lors de la mise à jour des dimensions');
    }
  };

  const handleEditSave = async () => {
    try {
      await litigesApi.update(litige.litige.id, editFormData);
      setIsEditing(false);
      onCommentAdded();
    } catch (err) {
      console.error('Erreur sauvegarde litige:', err);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleAnalyseDossier = async () => {
    setDossierAnalyseLoading(true);
    try {
      const res = await litigesApi.analyseDossier(litige.litige.id);
      setDossierAnalyse(res.data);
    } catch (err) {
      console.error('Erreur analyse IA dossier:', err);
      alert('Erreur lors de l\'analyse IA');
    } finally {
      setDossierAnalyseLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      setLoading(true);
      const res = await litigesApi.addCommentaire(litige.litige.id, newComment, newCommentDimension);
      const commentaireId = res.data.commentaire?.id;

      // Upload du doc lié si présent
      if (pendingDoc && commentaireId) {
        try {
          const file = pendingDoc.file;
          if (file.type === 'application/pdf') {
            const compressed = await compressPdfClientSide(file);
            const fd = new FormData();
            fd.append('file', compressed, compressed.name);
            fd.append('commentaire_id', commentaireId);
            await litigeDocsApi.uploadFile(litige.litige.id, fd);
          } else {
            const compressed = await compressFile(file);
            const presignRes = await litigeDocsApi.presign(litige.litige.id, {
              filename: compressed.name, contentType: compressed.type,
            });
            await fetch(presignRes.data.uploadUrl, {
              method: 'PUT', body: compressed, headers: { 'Content-Type': compressed.type },
            });
            await litigeDocsApi.confirm(litige.litige.id, {
              key: presignRes.data.key, filename: compressed.name,
              contentType: compressed.type, size: compressed.size,
              commentaire_id: commentaireId,
            });
          }
        } catch (docErr) {
          console.error('Erreur upload doc commentaire:', docErr);
        }
        if (pendingDoc.url?.startsWith('blob:')) URL.revokeObjectURL(pendingDoc.url);
        setPendingDoc(null);
      }

      setNewComment('');
      onCommentAdded();
      // Recharger les détails et documents
      const [detailsRes] = await Promise.all([
        litigesApi.getById(litige.litige.id),
        loadDocuments(),
      ]);
      litige.commentaires = detailsRes.data.commentaires;
    } catch (error) {
      console.error('Erreur ajout commentaire:', error);
      alert('Erreur lors de l\'ajout du commentaire');
    } finally {
      setLoading(false);
    }
  };

  const handleCommentDocDrop = (e) => {
    e.preventDefault();
    setDocDragging(false);
    const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (!file) return;
    if (pendingDoc?.url?.startsWith('blob:')) URL.revokeObjectURL(pendingDoc.url);
    setPendingDoc({ file, url: URL.createObjectURL(file), name: file.name });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📋 Détails du litige</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isAdmin && !isEditing && (
              <button type="button" className="btn-secondary" style={{ fontSize: '13px', padding: '5px 12px' }}
                onClick={() => setIsEditing(true)}>
                ✏️ Modifier
              </button>
            )}
            {isEditing && (
              <>
                <button type="button" className="btn-primary" style={{ fontSize: '13px', padding: '5px 12px' }}
                  onClick={handleEditSave}>
                  💾 Enregistrer
                </button>
                <button type="button" className="btn-secondary" style={{ fontSize: '13px', padding: '5px 12px' }}
                  onClick={() => setIsEditing(false)}>
                  ✖️ Annuler
                </button>
              </>
            )}
            <button type="button" onClick={onClose} className="modal-close">✕</button>
          </div>
        </div>

        <div className="modal-body" style={{ padding: 0 }}>
        <div className="page-tabs">
          <button
            className={activeTab === 'apercu' ? 'active' : ''}
            onClick={() => setActiveTab('apercu')}
          >
            Aperçu
          </button>
        </div>

        {/* ── Dimensions actives — juste sous les onglets ── */}
        <div className="details-dimensions-bar">
          {DIMENSIONS.map(dim => {
            const actif = localDimensions.includes(dim.key);
            return (
              <button key={dim.key} type="button"
                className={`dim-toggle-btn${actif ? ' dim-toggle-actif' : ''}`}
                style={actif ? { background: dim.couleur, color: '#fff', borderColor: dim.couleur } : {}}
                onClick={() => handleToggleDimension(dim.key)}
                title={dim.label}
              >
                <span className="dim-abbr">{dim.abbr}</span>
                <span className="dim-lbl">{dim.label}</span>
              </button>
            );
          })}
        </div>

        <div className="modal-tab-content">
        {activeTab === 'apercu' && (
        <div className="litige-details">
          {isSuperAdmin && (
            <div className="ia-dossier-bloc">
              <div className="ia-dossier-header">
                <span className="ia-dossier-title">🤖 Analyse IA du dossier</span>
                <button
                  className="btn-analyse-dossier"
                  onClick={handleAnalyseDossier}
                  disabled={dossierAnalyseLoading}
                >
                  {dossierAnalyseLoading ? 'Analyse en cours…' : dossierAnalyse ? '↻ Réanalyser' : 'Analyser'}
                </button>
              </div>
              {dossierAnalyseLoading && (
                <div className="ia-dossier-loading">
                  <div className="ia-spinner" />
                  <span>Analyse en cours…</span>
                </div>
              )}
              {dossierAnalyse && !dossierAnalyseLoading && (
                <div className="ia-dossier-result">
                  <div className={`ia-urgence ia-urgence-${dossierAnalyse.urgence?.toLowerCase()}`}>
                    {dossierAnalyse.urgence === 'HAUTE' ? '🔴' : dossierAnalyse.urgence === 'MOYENNE' ? '🟠' : '🟢'}
                    {' '}<strong>{dossierAnalyse.urgence}</strong> — {dossierAnalyse.urgence_raison}
                  </div>
                  <p className="ia-synthese">{dossierAnalyse.synthese}</p>
                  {dossierAnalyse.actions?.length > 0 && (
                    <div className="ia-actions">
                      <strong>Actions à mener :</strong>
                      <ol>
                        {dossierAnalyse.actions.map((a, i) => <li key={i}>{a}</li>)}
                      </ol>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {isEditing ? (
            <div className="litige-edit-form">
              <div className="form-row-2">
                <div className="form-group">
                  <label>Nom</label>
                  <input value={editFormData.nom} onChange={e => setEditFormData({...editFormData, nom: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Prénom</label>
                  <input value={editFormData.prenom} onChange={e => setEditFormData({...editFormData, prenom: e.target.value})} />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Téléphone</label>
                  <input value={editFormData.telephone} onChange={e => setEditFormData({...editFormData, telephone: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input value={editFormData.email} onChange={e => setEditFormData({...editFormData, email: e.target.value})} />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Immatriculation</label>
                  <input value={editFormData.immatriculation} onChange={e => setEditFormData({...editFormData, immatriculation: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Marque</label>
                  <input value={editFormData.marque} onChange={e => setEditFormData({...editFormData, marque: e.target.value})} />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Modèle</label>
                  <input value={editFormData.modele} onChange={e => setEditFormData({...editFormData, modele: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Responsable</label>
                  <select value={editFormData.responsable_id} onChange={e => setEditFormData({...editFormData, responsable_id: e.target.value})}>
                    <option value="">— Sélectionner —</option>
                    {responsables.map(r => (
                      <option key={r.id} value={r.id}>{r.nom} {r.prenom}</option>
                    ))}
                  </select>
                </div>
              </div>
              {!isOperateur && (
                <div className="form-group">
                  <label>Concession</label>
                  <select value={editFormData.concession_id} onChange={e => setEditFormData({...editFormData, concession_id: e.target.value})}>
                    <option value="">— Sélectionner —</option>
                    {concessions.map(c => (
                      <option key={c.id} value={c.id}>{c.nom}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          ) : (
          <div className="details-grid">
            <div>Client : {litige.litige.nom} {litige.litige.prenom}</div>
            <div>Téléphone : {litige.litige.telephone}</div>
            <div>Email : {litige.litige.email || '-'}</div>
            <div>Véhicule : {litige.litige.marque} {litige.litige.modele}</div>
            <div>Immat : {litige.litige.immatriculation}</div>
            <div>
              Dimension :
              {litige.litige.dimension_courante ? (() => {
                const dim = DIMENSIONS.find(d => d.key === litige.litige.dimension_courante);
                return dim ? (
                  <span
                    className="statut-badge"
                    style={{ background: dim.couleur, color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '11px', marginLeft: '8px' }}
                  >
                    {dim.label}
                  </span>
                ) : <span style={{ marginLeft: '8px', color: 'var(--text-muted)', fontSize: '12px' }}>—</span>;
              })() : <span style={{ marginLeft: '8px', color: 'var(--text-muted)', fontSize: '12px' }}>—</span>}
            </div>
            <div>Responsable : {litige.litige.responsable_nom} {litige.litige.responsable_prenom}</div>
            <div>Ouvert le : {litige.litige.date_ouverture ? new Date(litige.litige.date_ouverture).toLocaleDateString('fr-FR') : '-'}</div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div>
                <span style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Créé le</span>
                {isAdmin ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                    <input
                      type="date"
                      value={dateCreation}
                      onChange={e => setDateCreation(e.target.value)}
                      style={{ padding: '4px 8px', border: '1px solid var(--border-strong)', borderRadius: '6px', fontSize: '13px' }}
                    />
                    <button
                      onClick={handleSaveDateCreation}
                      disabled={savingDateCreation}
                      className="btn-primary"
                      style={{ padding: '4px 10px', fontSize: '12px' }}
                    >
                      {savingDateCreation ? '…' : 'Enregistrer'}
                    </button>
                  </span>
                ) : (
                  <div style={{ marginTop: '4px' }}>{litige.litige.created_at ? new Date(litige.litige.created_at).toLocaleDateString('fr-FR') : '-'}</div>
                )}
              </div>
              <div>
                <span style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Réception</span>
                {isAdmin ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                    <input
                      type="date"
                      value={dateReception}
                      onChange={e => setDateReception(e.target.value)}
                      style={{ padding: '4px 8px', border: '1px solid var(--border-strong)', borderRadius: '6px', fontSize: '13px' }}
                    />
                    <button
                      onClick={handleSaveDateReception}
                      disabled={savingDateReception}
                      className="btn-primary"
                      style={{ padding: '4px 10px', fontSize: '12px' }}
                    >
                      {savingDateReception ? '…' : 'Enregistrer'}
                    </button>
                  </span>
                ) : (
                  <div style={{ marginTop: '4px' }}>{litige.litige.date_reception ? new Date(litige.litige.date_reception).toLocaleDateString('fr-FR') : '-'}</div>
                )}
              </div>
              <div>
                <span style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>1er contact</span>
                {isAdmin ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                    <input
                      type="date"
                      value={datePremierContact}
                      onChange={e => setDatePremierContact(e.target.value)}
                      style={{ padding: '4px 8px', border: '1px solid var(--border-strong)', borderRadius: '6px', fontSize: '13px' }}
                    />
                    <button
                      onClick={handleSaveDatePremierContact}
                      disabled={savingDatePremierContact}
                      className="btn-primary"
                      style={{ padding: '4px 10px', fontSize: '12px' }}
                    >
                      {savingDatePremierContact ? '…' : 'Enregistrer'}
                    </button>
                  </span>
                ) : (
                  <div style={{ marginTop: '4px' }}>{litige.litige.date_premier_contact ? new Date(litige.litige.date_premier_contact).toLocaleDateString('fr-FR') : '-'}</div>
                )}
              </div>
              <div>
                <span style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Clôture</span>
                {isAdmin ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                    <input
                      type="date"
                      value={dateCloture}
                      onChange={e => setDateCloture(e.target.value)}
                      style={{ padding: '4px 8px', border: '1px solid var(--border-strong)', borderRadius: '6px', fontSize: '13px' }}
                    />
                    <button
                      onClick={handleSaveDateCloture}
                      disabled={savingDateCloture}
                      className="btn-primary"
                      style={{ padding: '4px 10px', fontSize: '12px' }}
                    >
                      {savingDateCloture ? '…' : 'Enregistrer'}
                    </button>
                  </span>
                ) : (
                  <div style={{ marginTop: '4px' }}>{litige.litige.date_cloture ? new Date(litige.litige.date_cloture).toLocaleDateString('fr-FR') : '-'}</div>
                )}
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              📅 Prochaine action :
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                <input
                  type="date"
                  value={dateAction}
                  onChange={e => setDateAction(e.target.value)}
                  style={{ padding: '6px 10px', border: '1px solid var(--border-strong)', borderRadius: '6px', fontSize: '14px' }}
                />
                <button
                  onClick={handleSaveDate}
                  disabled={savingDate}
                  className="btn-primary"
                  style={{ padding: '6px 14px', fontSize: '13px' }}
                >
                  {savingDate ? '…' : 'Enregistrer'}
                </button>
                {dateAction && (
                  <button
                    onClick={() => setDateAction('')}
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}
                    title="Effacer la date"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>
          )}

          {docsLoading ? (
            <div className="documents-loading">Chargement des documents...</div>
          ) : (
            <DocumentSection
              documents={documents}
              setDocuments={setDocuments}
              litigeId={litige.litige.id}
            />
          )}

          <div className="commentaires-section">
            <h3>💬 Historique des commentaires</h3>

            <div className="commentaires-list">
              {litige.commentaires.length === 0 ? (
                <p style={{ color: 'light-dark(#6c757d, var(--text-secondary))', fontStyle: 'italic' }}>Aucun commentaire</p>
              ) : (
                litige.commentaires.map(c => {
                  const cDocs = documents.filter(d => d.commentaire_id === c.id);
                  return (
                    <div key={c.id} className="commentaire-item">
                      <div className="commentaire-body">
                        <div className="commentaire-header">
                          <strong>{c.user_nom} {c.user_prenom}</strong>
                          {c.dimension && (() => {
                            const dim = DIMENSIONS.find(d => d.key === c.dimension);
                            return dim ? (
                              <span className="comment-dim-badge" style={{ background: dim.couleur }}>
                                {dim.label}
                              </span>
                            ) : null;
                          })()}
                          <span className="commentaire-date">
                            {new Date(c.created_at).toLocaleDateString('fr-FR')} à {new Date(c.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <div className="commentaire-text">{c.commentaire}</div>
                      </div>
                      {cDocs.length > 0 && (
                        <div className="commentaire-docs">
                          <CommentDocThumb doc={cDocs[0]} />
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            <div className="new-comment">
              <div className="new-comment-row">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Ajouter un commentaire..."
                  rows="3"
                  className="new-comment-textarea"
                />
                {/* Drop zone document */}
                <div
                  className={`comment-doc-drop${docDragging ? ' dragging' : ''}`}
                  onDragOver={e => { e.preventDefault(); setDocDragging(true); }}
                  onDragLeave={() => setDocDragging(false)}
                  onDrop={handleCommentDocDrop}
                  onClick={() => commentDocInputRef.current?.click()}
                >
                  {pendingDoc ? (
                    <div className="comment-doc-preview">
                      <CommentDocThumb doc={{ ...pendingDoc, url: pendingDoc.url, name: pendingDoc.name, type: pendingDoc.file.type }} />
                      <button className="comment-doc-remove" onClick={e => { e.stopPropagation(); if (pendingDoc.url?.startsWith('blob:')) URL.revokeObjectURL(pendingDoc.url); setPendingDoc(null); }}>✕</button>
                    </div>
                  ) : (
                    <>
                      <span className="comment-doc-icon">📎</span>
                      <span className="comment-doc-hint">Glisser un doc</span>
                    </>
                  )}
                  <input ref={commentDocInputRef} type="file" style={{ display: 'none' }}
                    accept="image/*,application/pdf"
                    onChange={handleCommentDocDrop} />
                </div>
              </div>
              <button
                className="btn-primary"
                onClick={handleAddComment}
                disabled={loading || !newComment.trim()}
              >
                {loading ? 'Ajout...' : '✅ Ajouter'}
              </button>
            </div>
          </div>
        </div>
        )}
        </div>{/* fin modal-tab-content */}
        </div>{/* fin modal-body */}

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Gestion des documents ───────────────────────────────────────────────────

// compressFile et compressPdfClientSide importés depuis ../utils/documents

/**
 * DocumentSection
 * - Sans litigeId : mode local (files gardés en state, uploadés au save du formulaire)
 * - Avec litigeId  : mode live (upload S3 immédiat au drop, suppression S3 au ✕)
 */
function DocumentSection({ documents, setDocuments, litigeId }) {
  const [dragging, setDragging] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [docToDelete, setDocToDelete] = useState(null);
  const fileInputRef = useRef(null);

  // MODE LIVE : upload S3 direct
  const uploadFile = useCallback(async (file) => {
    const tempId = Date.now() + Math.random();
    const tempDoc = {
      id: tempId, name: file.name, type: file.type,
      url: URL.createObjectURL(file), isLocal: true, uploading: true,
    };
    setDocuments(prev => [...prev, tempDoc]);

    try {
      let doc;
      if (file.type === 'application/pdf') {
        // PDF → compression canvas→JPEG client-side, puis upload backend (pdf-lib en 2e passe)
        const compressed = await compressPdfClientSide(file);
        const formData = new FormData();
        formData.append('file', compressed, compressed.name);
        const res = await litigeDocsApi.uploadFile(litigeId, formData);
        doc = normalizeDoc(res.data.document);
      } else {
        // Image → compression canvas côté client puis presign S3
        const compressed = await compressFile(file);
        const presignRes = await litigeDocsApi.presign(litigeId, {
          filename: compressed.name, contentType: compressed.type,
        });
        await fetch(presignRes.data.uploadUrl, {
          method: 'PUT', body: compressed, headers: { 'Content-Type': compressed.type },
        });
        const confirmRes = await litigeDocsApi.confirm(litigeId, {
          key: presignRes.data.key, filename: compressed.name,
          contentType: compressed.type, size: compressed.size,
        });
        doc = normalizeDoc(confirmRes.data.document);
      }

      URL.revokeObjectURL(tempDoc.url);
      setDocuments(prev => prev.map(d => d.id === tempId ? { ...doc, isLocal: false } : d));
    } catch (err) {
      console.error('Erreur upload:', err);
      setDocuments(prev => {
        const doc = prev.find(d => d.id === tempId);
        if (doc?.url?.startsWith('blob:')) URL.revokeObjectURL(doc.url);
        return prev.filter(d => d.id !== tempId);
      });
      alert(`Erreur lors de l'upload de "${file.name}"`);
    }
  }, [litigeId, setDocuments]);

  // MODE LOCAL : ajout en state (blob) — images compressées, PDFs compressés en tâche de fond
  const addLocalFile = useCallback(async (file) => {
    if (file.type === 'application/pdf') {
      // Ajout immédiat pour feedback, compression asynchrone en arrière-plan
      const tempId  = Date.now() + Math.random();
      const tempUrl = URL.createObjectURL(file);
      setDocuments(prev => [...prev, {
        id: tempId, file, name: file.name, type: file.type, url: tempUrl, isLocal: true,
      }]);
      compressPdfClientSide(file).then(compressed => {
        URL.revokeObjectURL(tempUrl);
        setDocuments(prev => prev.map(d =>
          d.id === tempId ? { ...d, file: compressed, url: URL.createObjectURL(compressed) } : d
        ));
      });
    } else {
      const processed = file.type.startsWith('image/') ? await compressFile(file) : file;
      setDocuments(prev => [...prev, {
        id: Date.now() + Math.random(),
        file: processed, name: processed.name, type: processed.type,
        url: URL.createObjectURL(processed), isLocal: true,
      }]);
    }
  }, [setDocuments]);

  // RENOMMAGE
  const renameDocument = useCallback(async (doc, newName) => {
    if (!doc.isLocal && litigeId) {
      try {
        await litigeDocsApi.rename(litigeId, doc.id, newName);
      } catch {
        alert('Erreur lors du renommage');
        return;
      }
    }
    setDocuments(prev => prev.map(d => d.id === doc.id ? { ...d, name: newName } : d));
  }, [litigeId, setDocuments]);

  const addFiles = useCallback((files) => {
    Array.from(files).forEach(file => {
      litigeId ? uploadFile(file) : addLocalFile(file);
    });
  }, [litigeId, uploadFile, addLocalFile]);

  const removeDocument = useCallback(async (doc) => {
    if (!doc.isLocal && litigeId) {
      try {
        await litigeDocsApi.delete(litigeId, doc.id);
      } catch (err) {
        alert('Erreur lors de la suppression du document');
        return;
      }
    }
    if (doc.url?.startsWith('blob:')) URL.revokeObjectURL(doc.url);
    setDocuments(prev => prev.filter(d => d.id !== doc.id));
  }, [litigeId, setDocuments]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  return (
    <div className="documents-section">
      <h4 className="documents-title">📎 Documents</h4>

      <div className="documents-grid">
        {documents.map(doc => (
          <DocumentThumbnail
            key={doc.id}
            doc={doc}
            onRemove={() => setDocToDelete(doc)}
            onClick={() => !doc.uploading && setLightbox(doc)}
            onRename={(newName) => renameDocument(doc, newName)}
          />
        ))}

        {/* Zone de dépôt — toujours visible en dernier */}
        <div
          className={`drop-zone${dragging ? ' dragging' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          title="Glisser-déposer ou cliquer pour ajouter un document"
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.msg"
            style={{ display: 'none' }}
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
          />
          <div className="drop-zone-content">
            <span className="drop-icon">📤</span>
            <span>Glisser-déposer<br />ou cliquer</span>
          </div>
        </div>
      </div>

      {lightbox && (
        <DocumentViewer doc={lightbox} onClose={() => setLightbox(null)} />
      )}

      {docToDelete && (
        <div className="doc-confirm-overlay" onClick={() => setDocToDelete(null)}>
          <div className="doc-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <p className="doc-confirm-msg">
              Supprimer le document&nbsp;?
            </p>
            <p className="doc-confirm-name">« {docToDelete.name} »</p>
            <div className="doc-confirm-actions">
              <button className="btn-secondary" onClick={() => setDocToDelete(null)}>
                Annuler
              </button>
              <button
                className="btn-danger"
                onClick={() => { removeDocument(docToDelete); setDocToDelete(null); }}
              >
                🗑️ Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// PdfThumbnail importé depuis ./shared/DocumentViewer

function DocumentThumbnail({ doc, onRemove, onClick, onRename }) {
  const mimeType = doc.type || doc.type_mime || '';
  const isImage  = mimeType.startsWith('image/');
  const isPdf    = mimeType === 'application/pdf';
  const isMsg    = mimeType === 'application/vnd.ms-outlook' || (doc.name || '').toLowerCase().endsWith('.msg');

  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName]   = useState(doc.name || '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!renaming) setNewName(doc.name || '');
  }, [doc.name, renaming]);

  useEffect(() => {
    if (renaming) inputRef.current?.focus();
  }, [renaming]);

  const submitRename = () => {
    const trimmed = newName.trim();
    if (trimmed && trimmed !== doc.name) onRename(trimmed);
    setRenaming(false);
  };

  return (
    <div
      className={`doc-thumbnail${doc.uploading ? ' uploading' : ''}`}
      title={doc.name}
    >
      {doc.uploading && (
        <div className="doc-uploading-overlay">
          <span className="doc-uploading-spinner" />
        </div>
      )}

      <button
        className="doc-remove-btn"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        title="Supprimer ce document"
        disabled={doc.uploading}
      >
        ✕
      </button>

      {/* Clic pour ouvrir l'aperçu — uniquement sur la zone image */}
      <div className="doc-preview" onClick={!doc.uploading ? onClick : undefined}>
        {isImage ? (
          <img src={doc.url} alt={doc.name} />
        ) : isPdf ? (
          <PdfThumbnail url={doc.url} />
        ) : isMsg ? (
          <div className="doc-icon">
            <span className="doc-icon-emoji">✉️</span>
            <span className="doc-icon-label">EMAIL</span>
          </div>
        ) : (
          <div className="doc-icon">
            <span className="doc-icon-emoji">📎</span>
            <span className="doc-icon-label">DOC</span>
          </div>
        )}
      </div>

      {renaming ? (
        <input
          ref={inputRef}
          className="doc-rename-input"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onBlur={submitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submitRename();
            if (e.key === 'Escape') { setNewName(doc.name || ''); setRenaming(false); }
          }}
        />
      ) : (
        <div
          className="doc-name"
          title="Double-cliquer pour renommer"
          onDoubleClick={() => { if (!doc.uploading) setRenaming(true); }}
        >
          {doc.name}
        </div>
      )}
    </div>
  );
}

// DocumentViewer importé depuis ./shared/DocumentViewer

// ─── Import Excel ─────────────────────────────────────────────────────────────

const IMPORT_COL_MAP = {
  'immatriculation': 'immatriculation', 'immat': 'immatriculation', 'plaque': 'immatriculation',
  'nom': 'nom',
  'prenom': 'prenom', 'prénom': 'prenom',
  'telephone': 'telephone', 'téléphone': 'telephone', 'tel': 'telephone', 'tél': 'telephone', 'mobile': 'telephone',
  'email': 'email', 'e-mail': 'email', 'mail': 'email', 'courriel': 'email',
  'marque': 'marque',
  'modele': 'modele', 'modèle': 'modele', 'model': 'modele',
  'statut': 'statut',
  'responsable': 'responsable',
  'concession': 'concession',
  'date prochaine action': 'date_prochaine_action',
  'date_prochaine_action': 'date_prochaine_action',
  'prochaine action': 'date_prochaine_action',
  'commentaire initial': 'commentaire_initial',
  'commentaire_initial': 'commentaire_initial',
  'commentaire': 'commentaire_initial',
  'comment': 'commentaire_initial',
};

function ImportModal({ onClose, onDone }) {
  const [step, setStep]     = useState('pick'); // pick | preview | done
  const [rows, setRows]     = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const parseFile = async (file) => {
    const XLSX = await import('xlsx');
    const data = await file.arrayBuffer();
    const wb   = XLSX.read(data, { type: 'array', cellDates: true });
    const ws   = wb.Sheets[wb.SheetNames[0]];
    const raw  = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    if (raw.length < 2) return [];

    const headers     = raw[0].map(h => h.toString().toLowerCase().trim());
    const fieldKeys   = headers.map(h => IMPORT_COL_MAP[h] || null);

    return raw.slice(1)
      .filter(r => r.some(v => v !== '' && v !== null))
      .map(row => {
        const obj = {};
        fieldKeys.forEach((field, i) => {
          if (!field) return;
          let val = row[i];
          if (val === '' || val === null || val === undefined) return;
          if (val instanceof Date) {
            val = val.toISOString().slice(0, 10);
          } else {
            val = val.toString().trim();
          }
          if (val) obj[field] = val;
        });
        return obj;
      })
      .filter(r => Object.keys(r).length > 0);
  };

  const handleFile = async (file) => {
    try {
      const parsed = await parseFile(file);
      setRows(parsed);
      setStep('preview');
    } catch {
      alert('Impossible de lire le fichier. Vérifiez le format (.xlsx, .xls, .csv).');
    }
  };

  const handleImport = async () => {
    setLoading(true);
    try {
      const res = await litigesApi.importRows(rows);
      setResult(res.data);
      setStep('done');
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'import');
    } finally {
      setLoading(false);
    }
  };

  const PREVIEW_COLS = [
    { key: 'immatriculation', label: 'Immat' },
    { key: 'nom',             label: 'Nom' },
    { key: 'prenom',          label: 'Prénom' },
    { key: 'telephone',       label: 'Tél' },
    { key: 'marque',          label: 'Marque' },
    { key: 'modele',          label: 'Modèle' },
    { key: 'statut',               label: 'Statut' },
    { key: 'responsable',          label: 'Responsable' },
    { key: 'commentaire_initial',  label: 'Commentaire initial' },
  ];

  return (
    <div className="modal-overlay">
      <div className="modal-content import-modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📥 Import Excel — Litiges</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {step === 'pick' && (
          <div className="modal-body import-pick-body">
            <p className="import-hint">
              Colonnes reconnues (noms libres, insensibles à la casse) :<br />
              <strong>Immatriculation, Nom, Prénom, Téléphone, Email, Marque, Modèle, Statut, Responsable, Concession, Date prochaine action, Commentaire initial</strong>
            </p>
            <p className="import-hint">
              Seule l'immatriculation est obligatoire.<br />
              Si elle existe déjà → mise à jour des champs fournis. Sinon → création.
            </p>
            <button className="btn-primary import-pick-btn" onClick={() => fileRef.current?.click()}>
              📂 Choisir un fichier Excel
            </button>
            <div className="import-formats">.xlsx · .xls · .csv</div>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
              onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); e.target.value = ''; }} />
          </div>
        )}

        {step === 'preview' && (
          <>
            <div className="modal-body import-preview-body">
              <p className="import-preview-count">
                <strong>{rows.length} ligne{rows.length !== 1 ? 's' : ''}</strong> détectée{rows.length !== 1 ? 's' : ''} — vérifiez avant d'importer
              </p>
              <div className="import-table-wrap">
                <table className="import-table">
                  <thead>
                    <tr>{PREVIEW_COLS.map(c => <th key={c.key}>{c.label}</th>)}</tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        {PREVIEW_COLS.map(c => (
                          <td key={c.key}>{r[c.key] || <span className="import-empty">—</span>}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setStep('pick')}>← Changer de fichier</button>
              <button className="btn-primary" onClick={handleImport} disabled={loading || rows.length === 0}>
                {loading ? 'Import en cours…' : `✅ Importer ${rows.length} ligne${rows.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </>
        )}

        {step === 'done' && result && (
          <div className="modal-body import-done-body">
            <div className="import-done-icon">✅</div>
            <div className="import-stats">
              <div className="import-stat import-stat--created">
                <strong>{result.created}</strong>
                <span>Créé{result.created !== 1 ? 's' : ''}</span>
              </div>
              <div className="import-stat import-stat--updated">
                <strong>{result.updated}</strong>
                <span>Mis à jour</span>
              </div>
              {result.skipped > 0 && (
                <div className="import-stat import-stat--skipped">
                  <strong>{result.skipped}</strong>
                  <span>Ignoré{result.skipped !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
            {result.errors?.length > 0 && (
              <div className="import-errors">
                {result.errors.map((e, i) => <div key={i}>Ligne {e.ligne} : {e.raison}</div>)}
              </div>
            )}
            <button className="btn-primary" onClick={() => { onDone(); onClose(); }}>
              Fermer et actualiser
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Litiges;
